import { AUDIT_ACTIONS, TARGET_TYPES, type AuditAction, type TargetType } from '@hirebotiq-ops/core';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { AdminLayout } from '../components/AdminLayout.js';
import { Badge } from '../components/ui/badge.js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card.js';
import { Button } from '../components/ui/button.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table.js';
import { requireRole } from '../lib/auth-guard.js';
import { trpc } from '../lib/trpc.js';

export const Route = createFileRoute('/admin/audit')({
  component: AdminAudit,
  beforeLoad: async ({ context }) => {
    await requireRole(context.queryClient, ['ceo', 'internal_staff']);
  },
});

type ActionFilter = 'all' | AuditAction;
type TargetFilter = 'all' | TargetType;

const PAGE_SIZE = 100;

function AdminAudit() {
  const [action, setAction] = useState<ActionFilter>('all');
  const [target, setTarget] = useState<TargetFilter>('all');
  const [offset, setOffset] = useState(0);

  const list = trpc.admin.auditAll.useQuery({
    action: action === 'all' ? undefined : action,
    targetType: target === 'all' ? undefined : target,
    limit: PAGE_SIZE,
    offset,
  });

  return (
    <AdminLayout>
      <div className="space-y-4">
        <header>
          <h1 className="text-3xl font-bold">Audit-Log</h1>
          <p className="text-[var(--muted-foreground)]">
            Alle Status-Wechsel, Rollenzuweisungen und Reviews — chronologisch
          </p>
        </header>

        <Card>
          <CardHeader>
            <CardTitle>Filter</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 md:grid-cols-2">
            <Select
              value={action}
              onValueChange={(v) => {
                setAction(v as ActionFilter);
                setOffset(0);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Aktion" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Aktionen</SelectItem>
                {AUDIT_ACTIONS.map((a) => (
                  <SelectItem key={a} value={a}>
                    {a}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={target}
              onValueChange={(v) => {
                setTarget(v as TargetFilter);
                setOffset(0);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Target-Typ" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Target-Typen</SelectItem>
                {TARGET_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-44">Zeit</TableHead>
                  <TableHead>Actor</TableHead>
                  <TableHead>Aktion</TableHead>
                  <TableHead>Target</TableHead>
                  <TableHead>Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.isLoading && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-[var(--muted-foreground)]">
                      Lade …
                    </TableCell>
                  </TableRow>
                )}
                {list.data?.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-[var(--muted-foreground)]">
                      Keine Einträge.
                    </TableCell>
                  </TableRow>
                )}
                {list.data?.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell className="text-xs whitespace-nowrap">
                      {new Date(entry.createdAt).toLocaleString('de-DE')}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">{entry.actorName ?? '—'}</div>
                      <div className="text-xs text-[var(--muted-foreground)] font-mono">
                        {entry.actorEmail ?? entry.actorId.slice(0, 8)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{entry.action}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">{entry.targetType}</div>
                      <div className="text-xs text-[var(--muted-foreground)] font-mono">
                        {entry.targetId.slice(0, 8)}
                      </div>
                    </TableCell>
                    <TableCell>
                      {entry.payload ? (
                        <details className="text-xs">
                          <summary className="cursor-pointer text-[var(--muted-foreground)]">
                            Payload
                          </summary>
                          <pre className="mt-2 max-w-md overflow-x-auto rounded bg-[var(--secondary)] p-2 font-mono">
                            {JSON.stringify(entry.payload, null, 2)}
                          </pre>
                        </details>
                      ) : (
                        <span className="text-xs text-[var(--muted-foreground)]">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <div className="mt-4 flex items-center justify-between">
              <p className="text-xs text-[var(--muted-foreground)]">
                Einträge {offset + 1}–{offset + (list.data?.length ?? 0)}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={offset === 0}
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                >
                  Zurück
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={(list.data?.length ?? 0) < PAGE_SIZE}
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                >
                  Weiter
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
